import MaterialReactTable, {
  MRT_FullScreenToggleButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton,
} from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IIncomesForCashResponse } from '../../apis/models/transactions';
import DownloadIcon from '@mui/icons-material/Download';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { GroupedCashReportData } from './stats-report';
import AccountBreakdown from '../../components/AccountBreakdown';
import { IExpensesForCashResponse } from '../../apis/models/transactions';
import { downloadCombinedCashReport } from './downloadCashReport';

const ToggleGlobalFilter = MRT_ToggleGlobalFilterButton;
const ToggleFilters = MRT_ToggleFiltersButton;
const ShowHideColumns = MRT_ShowHideColumnsButton;
const FullScreenToggle = MRT_FullScreenToggleButton;

interface ICashReportHistoryProps {
  incomesTransactionResult: IIncomesForCashResponse[];
  expenseTransactionResult: IExpensesForCashResponse[];
  groupedIncomesStats?: GroupedCashReportData[];
}

const IncomesCashReportDetails: React.FC<ICashReportHistoryProps> = ({
  incomesTransactionResult,
  expenseTransactionResult,
  groupedIncomesStats,
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const isFr = currentLanguage.includes('fr');
  const [groupedIncomes, setGroupedIncomes] = useState<GroupedCashReportData[] | undefined>();
  const incomeFeeTypes = useSelector((state: RootState) =>
    state.feeTypes.other.concat(state.feeTypes.student)
  );
  const expenseFeeTypes = useSelector((state: RootState) => state.feeTypes.expenses);

  const resolveFeeType = (enumKey: string): string => {
    const found = [...incomeFeeTypes, ...expenseFeeTypes].find((ft) => ft.enum_key === enumKey);
    if (found) return isFr ? found.value_fr : found.value_en;
    return enumKey;
  };
  useEffect(() => {
    if (groupedIncomesStats && groupedIncomesStats.length > 0) {
      const groupedIncomeData = groupedIncomesStats.map((item) => {
        const translatedValue = incomeFeeTypes.find((feeType) => {
          return feeType && feeType.enum_key && feeType.enum_key === item.key;
        });
        return {
          ...item,
          translatedKey:
            currentLanguage.indexOf('fr') >= 0
              ? translatedValue?.value_fr || 'Compte non trouvé'
              : translatedValue?.value_en || 'Account not found',
        };
      });
      setGroupedIncomes(groupedIncomeData);
    }
  }, [groupedIncomesStats, incomeFeeTypes, currentLanguage]);
  const transactionsResult = useMemo(() => {
    return incomesTransactionResult.map((row) => {
      const translatedValue = incomeFeeTypes.find((item) => {
        return item && item.enum_key && item.enum_key === row.feeType;
      });
      return {
        ...row,
        feeType:
          currentLanguage.indexOf('fr') > 0
            ? translatedValue?.value_fr || 'Compte non trouvé'
            : translatedValue?.value_en || 'Account not found',
      };
    });
  }, [incomeFeeTypes, incomesTransactionResult, currentLanguage]);

  const downloadReport = (table: any) => {
    const filteredIncomes = table.getFilteredRowModel().rows.map((row: any) => row.original);
    // Incomes tab downloads incomes only.
    downloadCombinedCashReport(
      filteredIncomes,
      [],
      resolveFeeType,
      {
        incomesTitle: t('reports_breakdown.incomes'),
        expensesTitle: t('reports_breakdown.expenses'),
        date: t('tables.date'),
        names: t('tables.names'),
        description: t('tables.description'),
        cashier: t('tables.employee_name'),
        amount: t('tables.amount'),
        beneficiary: t('tables.beneficiary_name'),
        authoriser: t('tables.authoriser_name'),
        subtotal: t('tables.subtotal'),
      },
      'cash-report.pdf'
    );
  };
  const columns = useMemo(
    () => [
      {
        header: t('tables.expense_code'),
        accessorKey: 'feeType',
      },
      {
        header: t('tables.date'),
        accessorKey: 'groupId',
      },
      {
        header: t('tables.amount'),
        accessorKey: 'fee_amount',

        aggregationFn: 'sum',
        AggregatedCell: ({ cell, table }) => {
          const colorValue = cell.getValue() > 0 ? 'success.main' : 'error.main';
          return (
            <>
              {t('tables.sum_by') +
                ' ' +
                table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}
              :{' '}
              <Box sx={{ color: colorValue, fontWeight: 'bold' }}>
                {cell.getValue()?.toLocaleString?.('fr-FR', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Box>
            </>
          );
        },
      },
      {
        header: t('tables.names'),
        accessorKey: 'names',
      },
    ],
    [t]
  );

  const total = useMemo(() => {
    const total = transactionsResult.reduce((a, b) => a + b.fee_amount, 0);
    return total.toFixed(2);
  }, [transactionsResult]);
  return (
    <>
      <Typography mb={1} variant="h3">
        {t('income_lbl') + total + '$'}
      </Typography>
      <MaterialReactTable
        // @ts-ignore
        columns={columns}
        data={transactionsResult}
        renderToolbarInternalActions={({ table }) => (
          <>
            <ToggleGlobalFilter table={table} />
            <IconButton onClick={() => downloadReport(table)}>
              <DownloadIcon />
            </IconButton>
            <ToggleFilters table={table} />
            <ShowHideColumns table={table} />
            <FullScreenToggle table={table} />
          </>
        )}
        enableColumnResizing
        enableGrouping
        enableStickyHeader
        enableStickyFooter
        initialState={{
          density: 'compact',
          expanded: true, //expand all groups by default
          grouping: ['feeType'], //an array of columns to group by default (can be multiple)
          pagination: { pageIndex: 0, pageSize: 15 },
          sorting: [{ id: 'groupId', desc: false }], //sort by state by default
        }}
        muiToolbarAlertBannerChipProps={{ color: 'primary' }}
        muiTableContainerProps={{ sx: { maxHeight: 700 } }}
      />
      <Box mt={1}>
        {groupedIncomes && groupedIncomes.length > 0 && <AccountBreakdown data={groupedIncomes} />}
      </Box>
    </>
  );
};
export default IncomesCashReportDetails;
